import {useState, useEffect} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useActiveChild} from "./ActiveChildContext";
import {setDoc, doc, Timestamp, updateDoc, getDoc, deleteDoc} from "firebase/firestore";
import {ulid} from "ulid";
import {db} from "../firebase";
import {requireEntryAccess} from "../utils/childAccess";
import {showToast} from "../utils/toast";
import {LayoutGrid} from "lucide-react";
import {
	WaBreadcrumb,
	WaBreadcrumbItem,
	WaInput,
	WaSelect,
	WaOption,
	WaTextarea,
	WaButton,
	WaNumberInput,
} from "@web.awesome.me/webawesome-pro/dist/react";

import Header from "./Header";

const getInitialDateTime = () => {
	const now = new Date();
	const pad = (value) => String(value).padStart(2, "0");
	const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
	const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
	return {date, time};
};

const GrowthEntryPage = () => {
	const {entryId} = useParams();
	const initialDateTime = getInitialDateTime();
	const [date, setDate] = useState(initialDateTime.date);
	const [height, setHeight] = useState("");
	const [weightLbs, setWeightLbs] = useState("");
	const [weightOz, setWeightOz] = useState("");
	const [note, setNote] = useState("");
	const navigate = useNavigate();

	const {activeChild, activeChildId, children, selectChild, loadingChildren} = useActiveChild();

	const handleSave = async () => {
		if (!activeChildId) {
			await showToast("Please select a child before saving.", "warning");
			return;
		}

		const timestamp = Timestamp.fromDate(new Date(`${date}T00:00:00`));
		const payload = {
			child_id: activeChildId,
			timestamp,
			height,
			weightLbs,
			weightOz,
			note,
		};

		try {
			if (entryId) {
				const entryRef = doc(db, "growth", entryId);
				const entrySnap = await getDoc(entryRef);
				const canAccess = await requireEntryAccess({
					entrySnap,
					children,
					showToast,
					navigate,
					redirectTo: "/growth-history",
				});
				if (!canAccess) {
					return;
				}
				await updateDoc(entryRef, payload);
			} else {
				const id = ulid();
				await setDoc(doc(db, "growth", id), payload);
			}

			navigate("/growth-history");
		} catch (error) {
			console.error("Error saving growth entry:", error);
			await showToast("Could not save growth entry. Please try again.", "danger");
		}
	};
	const handleCancel = () => navigate("/growth-history");

	const handleDelete = async () => {
		if (!entryId) return;
		if (confirm("Are you sure you want to delete this entry?")) {
			try {
				const entryRef = doc(db, "growth", entryId);
				const entrySnap = await getDoc(entryRef);
				const canAccess = await requireEntryAccess({
					entrySnap,
					children,
					showToast,
					navigate,
					redirectTo: "/growth-history",
				});
				if (!canAccess) {
					return;
				}
				await deleteDoc(entryRef);
				navigate("/growth-history");
			} catch (error) {
				console.error("Error deleting growth entry:", error);
				await showToast("Could not delete growth entry. Please try again.", "danger");
			}
		}
	};

	useEffect(() => {
		const fetchEntry = async () => {
			if (loadingChildren) return;
			if (!entryId) return;
			const docRef = doc(db, "growth", entryId);
			const docSnap = await getDoc(docRef);
			if (docSnap.exists()) {
				const canAccess = await requireEntryAccess({
					entrySnap: docSnap,
					children,
					showToast,
					navigate,
					redirectTo: "/growth-history",
					selectChild,
					activeChildId,
					syncActiveChild: true,
				});
				if (!canAccess) {
					return;
				}
				const data = docSnap.data();
				// Convert Firestore Timestamp to local date and time strings
				if (data.timestamp) {
					const startDateObj = new Date(data.timestamp.seconds * 1000);
					setDate(startDateObj.getFullYear() + "-" + String(startDateObj.getMonth() + 1).padStart(2, "0") + "-" + String(startDateObj.getDate()).padStart(2, "0"));
				}
				setNote(data.note || "");
				setHeight(data.height || "");
				setWeightLbs(data.weightLbs || "");
				setWeightOz(data.weightOz || "");
			}
		};
		fetchEntry();
	}, [entryId, children, loadingChildren]);

	return (
		<div className="page">
			<Header
				activePage="growth"
				title={entryId ? "Edit Growth Entry" : "Add Growth Entry"}
			/>
			<div className="page-meta">
				<WaBreadcrumb>
					<WaBreadcrumbItem href="/">
						<LayoutGrid size={24} />
					</WaBreadcrumbItem>
					<WaBreadcrumbItem href={`/growth-history/`}>
						{activeChild ? activeChild.nickname + "'s " : ""}Growth
					</WaBreadcrumbItem>
					<WaBreadcrumbItem href={`/growth/${entryId}`}>
						{entryId ? "Edit" : "Add"}
					</WaBreadcrumbItem>
				</WaBreadcrumb>
			</div>
			<div className="page-content">
				<WaInput
					className=""
					label="Date"
					type="date"
					name="date"
					placeholder="Enter date"
					value={date}
					size="large"
					onInput={(e) => setDate(e.target.value)}
				></WaInput>

				<div className="elem-group gap-xl flex-wrap full-width">
					<WaNumberInput
						label="Weight (lbs)"
						value={weightLbs}
						onInput={(e) => setWeightLbs(e.target.value)}
						size="large"
						className="full-width"
						max="200"
						min="0"
					/>
					<WaNumberInput
						label="Weight (oz)"
						value={weightOz}
						onInput={(e) => setWeightOz(e.target.value)}
						size="large"
						max="14"
						min="0"
					/>
				</div>

				<WaNumberInput
					label="Height (in)"
					value={height}
					onInput={(e) => setHeight(e.target.value)}
					size="large"
					step="0.5"
					min="0"
					max="96"
					className="full-width"
				/>

				<WaTextarea
					label="Note"
					placeholder="Add any notes about this growth entry..."
					value={note}
					onInput={(e) => setNote(e.target.value)}
					hint="e.g. 'Baby seemed fussy during measurement', 'Baby measured for a long time', etc."
					size="large"
					resize="auto"
					rows="2"
					// with-count
					maxlength="100"
					className="full-width"
				/>
				<div className="page-footer">
					<div className="elem-group gap-sm">
						<WaButton
							className="btn-accent"
							onClick={handleSave}
							size="large"
							pill
						>
							Save
						</WaButton>
						<WaButton
							className="btn-gloss"
							onClick={handleCancel}
							size="large"
							pill
						>
							Cancel
						</WaButton>
					</div>
					{entryId && (
						<WaButton
							className="btn-outline"
							onClick={handleDelete}
							size="large"
							pill
						>
							Delete
						</WaButton>
					)}
				</div>
			</div>
		</div>
	);
};

export default GrowthEntryPage;
